/**
 * Client-side session (replaces next-auth on Vite — no server runtime).
 *
 * next-auth was only ever a thin client wrapper here: its `authorize` just
 * `decodeJwt`'d a login token, so there was no server validation to lose.
 * This module keeps the exact `useSession` / `signIn` / `signOut` /
 * `SessionProvider` API the app already calls, but backs it with the JWT
 * held in `safeStorage` (kit storage discipline) + React context.
 *
 * The login API integration is unchanged (per dev): the login screen still
 * calls the backend to obtain `loginToken`, then calls `signIn("credentials",
 * { loginToken, merchantId })` to establish the client session.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { decodeJwt } from "@/lib/jwt";
import { resolveScope, resolveStaffIds } from "@/lib/authScope";
import { safeStorage } from "@/shared/lib/safeStorage";

const TOKEN_KEY = "paygate_token";
const MERCHANT_KEY = "paygate_merchantId";

export type SessionUser = {
  name?: string;
  username?: string;
  role?: string;
  /** Resolved app scope (Source) — staffType-driven, PGLX-source fallback. */
  scope?: string;
  /** Commission agent flag from the JWT (dev parity — unlocks commission nav). */
  isAgent?: boolean;
  merchantId?: string;
  accessToken?: string;
  clientId?: number;
  clinicId?: number;
  employerId?: number;
  groupId?: number;
  divisionId?: number;
  locationId?: number;
  memberId?: number;
};

export type Session = { user: SessionUser } | null;

/**
 * There is no "loading": the session is read synchronously from storage on the
 * first render, so it is known before anything can ask. Callers that used to
 * branch on a pending state can render straight away.
 */
export type SessionStatus = "authenticated" | "unauthenticated";

type SignInResult = { ok: boolean; error?: string };

function buildUserFromToken(token: string, merchantId?: string): SessionUser | null {
  const payload = decodeJwt(token) as Record<string, unknown> | null;
  if (!payload) return null;
  const p = payload as Record<string, unknown> & {
    staffType?: string;
    staffTypeId?: number;
    source?: string;
    role?: string;
    isAgent?: boolean;
  };
  const ids = resolveStaffIds({
    staffType: p.staffType,
    staffTypeId: p.staffTypeId,
    clientId: p.clientId as number | undefined,
    clinicId: p.clinicId as number | undefined,
    employerId: p.employerId as number | undefined,
    groupId: p.groupId as number | undefined,
    divisionId: p.divisionId as number | undefined,
    locationId: p.locationId as number | undefined,
    memberId: p.memberId as number | undefined,
  });
  return {
    name: p.sub as string | undefined,
    username: p.sub as string | undefined,
    role: p.role as string | undefined,
    scope: resolveScope({
      staffType: p.staffType,
      source: p.source,
      targetDatabase: p.targetDatabase as string | undefined,
      role: p.role,
    }),
    isAgent: p.isAgent === true,
    merchantId:
      (p.merchantId as string | undefined) ??
      (p.targetDatabase as string | undefined) ??
      merchantId,
    accessToken: token,
    ...ids,
  };
}

function readSession(): Session {
  const token = safeStorage.getItem(TOKEN_KEY);
  if (!token) return null;
  const merchantId = safeStorage.getItem(MERCHANT_KEY) ?? undefined;
  const user = buildUserFromToken(token, merchantId);
  return user ? { user } : null;
}

type Ctx = {
  data: Session;
  status: SessionStatus;
  update: () => void;
};

const SessionContext = createContext<Ctx | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  // Seeded on the very first render, not in an effect. Reading the token is a
  // synchronous `localStorage` hit, so deferring it bought nothing and cost a
  // full-screen "loading" frame on every hard load.
  const [data, setData] = useState<Session>(readSession);

  const refresh = useCallback(() => setData(readSession()), []);

  useEffect(() => {
    // Another tab signing in or out should not leave this one authenticated.
    const onStorage = (e: StorageEvent) => {
      if (e.key === TOKEN_KEY || e.key === null) refresh();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refresh]);

  const value = useMemo<Ctx>(
    () => ({
      data,
      status: data ? "authenticated" : "unauthenticated",
      update: refresh,
    }),
    [data, refresh]
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** Drop-in replacement for next-auth's `useSession()`. */
export function useSession(): { data: Session; status: SessionStatus; update: () => void } {
  const ctx = useContext(SessionContext);
  if (!ctx) return { data: null, status: "unauthenticated", update: () => {} };
  return ctx;
}

/**
 * Drop-in replacement for next-auth's `signIn`. Only the "credentials"
 * provider (loginToken + merchantId) is supported — the app's own login
 * API produced that token before calling this.
 */
export async function signIn(
  _provider: string,
  opts?: { loginToken?: string; merchantId?: string; callbackUrl?: string; redirect?: boolean }
): Promise<SignInResult> {
  const token = opts?.loginToken;
  if (!token) return { ok: false, error: "missing loginToken" };
  const user = buildUserFromToken(token, opts?.merchantId);
  if (!user) return { ok: false, error: "invalid token" };
  safeStorage.setItem(TOKEN_KEY, token);
  if (opts?.merchantId) safeStorage.setItem(MERCHANT_KEY, opts.merchantId);
  // Notify same-tab listeners (SessionProvider) + other tabs.
  try {
    window.dispatchEvent(new StorageEvent("storage", { key: TOKEN_KEY, newValue: token }));
  } catch {
    /* StorageEvent constructor unavailable — provider still refreshes on mount */
  }
  if (opts?.redirect !== false && opts?.callbackUrl) {
    window.location.assign(opts.callbackUrl);
  }
  return { ok: true };
}

/** Drop-in replacement for next-auth's `signOut`. */
export async function signOut(opts?: { callbackUrl?: string; redirect?: boolean }): Promise<void> {
  safeStorage.removeItem(TOKEN_KEY);
  safeStorage.removeItem(MERCHANT_KEY);
  // The commission screens' shared scope belongs to this login (see useCommissionScope).
  safeStorage.removeItem("paygate_commission_scope_v1");
  try {
    window.dispatchEvent(new StorageEvent("storage", { key: TOKEN_KEY, newValue: null }));
  } catch {
    /* ignore */
  }
  window.location.assign(opts?.callbackUrl ?? "/login");
}

/** Read the raw access token outside React (for axios interceptors, etc.). */
export function getAccessToken(): string | null {
  return safeStorage.getItem(TOKEN_KEY);
}

/**
 * Drop-in replacement for next-auth's async `getSession()` — reads the
 * current client session from storage (used by axios/RTKQ to attach the
 * bearer token outside React).
 */
export async function getSession(): Promise<Session> {
  return readSession();
}
